import { createLoader } from '@metorial/data-hooks';
import { autoPaginate } from '../../lib/autoPaginate';
import { withAuth } from '../../user';

// BlackOS usage summary (Settings › Usage, which replaces the cloud's Billing
// page). Upstream's usage timeline is fed by the cloud's metering, which the
// open-source build doesn't ingest, so this counts the real records instead:
// sessions and tool calls created in the window, and portal accounts, for
// every instance of the organization. Lists are paged up to `cap` items per
// instance; counts at the cap are shown as "N+".

export type UsageSummaryDay = { day: string; sessions: number; toolCalls: number };

export type UsageSummary = {
  from: string;
  to: string;
  cap: number;
  sessions: { count: number; capped: boolean };
  toolCalls: { count: number; capped: boolean };
  portalUsers: number;
  days: UsageSummaryDay[];
  instances: {
    instanceId: string;
    sessions: number;
    toolCalls: number;
    portalUsers: number;
    capped: boolean;
  }[];
};

let dayKey = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export let usageSummaryLoader = createLoader({
  name: 'usageSummary',
  fetch: (i: { instanceIds: string[]; days: number; cap?: number }) =>
    withAuth(async sdk => {
      let cap = i.cap ?? 2000;
      let to = new Date();
      let from = new Date(to);
      from.setHours(0, 0, 0, 0);
      from.setDate(from.getDate() - (i.days - 1));

      let perInstance = await Promise.all(
        i.instanceIds.map(async instanceId => {
          let [sessions, toolCalls, counts] = await Promise.all([
            autoPaginate(
              cursor =>
                sdk.sessions.list(instanceId, { ...cursor, createdAt: { gt: from } }) as any,
              undefined,
              cap
            ).catch(() => [] as any[]),
            autoPaginate(
              cursor =>
                sdk.sessions.toolCalls.list(instanceId, {
                  ...cursor,
                  createdAt: { gt: from }
                }) as any,
              undefined,
              cap
            ).catch(() => [] as any[]),
            sdk.resourceCounts
              .get(instanceId, { resource: ['accounts'] })
              .catch(() => ({ resources: [] as { resource: string; count: number }[] }))
          ]);

          return {
            instanceId,
            sessions: sessions as { createdAt: Date }[],
            toolCalls: toolCalls as { createdAt: Date }[],
            portalUsers: counts.resources.find(r => r.resource == 'accounts')?.count ?? 0
          };
        })
      );

      let days = new Map<string, UsageSummaryDay>();
      for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
        let key = dayKey(d);
        days.set(key, { day: key, sessions: 0, toolCalls: 0 });
      }

      for (let inst of perInstance) {
        for (let s of inst.sessions) {
          let entry = days.get(dayKey(new Date(s.createdAt)));
          if (entry) entry.sessions++;
        }
        for (let t of inst.toolCalls) {
          let entry = days.get(dayKey(new Date(t.createdAt)));
          if (entry) entry.toolCalls++;
        }
      }

      let sessionsCapped = perInstance.some(p => p.sessions.length >= cap);
      let toolCallsCapped = perInstance.some(p => p.toolCalls.length >= cap);

      return {
        from: from.toISOString(),
        to: to.toISOString(),
        cap,
        sessions: {
          count: perInstance.reduce((n, p) => n + p.sessions.length, 0),
          capped: sessionsCapped
        },
        toolCalls: {
          count: perInstance.reduce((n, p) => n + p.toolCalls.length, 0),
          capped: toolCallsCapped
        },
        portalUsers: perInstance.reduce((n, p) => n + p.portalUsers, 0),
        days: [...days.values()],
        instances: perInstance.map(p => ({
          instanceId: p.instanceId,
          sessions: p.sessions.length,
          toolCalls: p.toolCalls.length,
          portalUsers: p.portalUsers,
          capped: p.sessions.length >= cap || p.toolCalls.length >= cap
        }))
      } satisfies UsageSummary;
    }),
  mutators: {}
});

export let useUsageSummary = (
  instanceIds: string[] | null | undefined,
  opts: { days: number; cap?: number }
) =>
  usageSummaryLoader.use(
    instanceIds && instanceIds.length
      ? { instanceIds: [...instanceIds].sort(), days: opts.days, cap: opts.cap }
      : null
  );
